import React, { useEffect } from 'react'
import styled from 'styled-components'
import { path } from 'ramda'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { firestoreConnect } from 'react-redux-firebase'
import { withHandlers } from 'recompose'
import { withStyles } from '@material-ui/core/styles'
import { property, map } from 'lodash'
import Type from '../Type'
import L from '../L'
import { SORT } from '../../Containers/MXCollectionPage/MXCollectionPage'
import {
  WEBSITE_CONFIG_COLLECTION,
  WEBSITE_CONFIG_ID,
  COLLECTIONS_COLLECTION,
  SIMULATIONS_COLLECTION,
  PRODUCTION,
} from '../../Config'
import SimulationBanner from '../SimulationBanner'
import IconArrowLeftDark from '../../Icons/IconArrowLeftDark'
import IconLogoSimple from '../../Icons/IconLogoSimple'
import AccountActions from '../../Redux/AccountRedux'
import searchResultsStyles from './SearchResults-styles'

const enhance = compose(
  firestoreConnect([
    {
      collection: WEBSITE_CONFIG_COLLECTION,
      where: ['id', '==', WEBSITE_CONFIG_ID],
    },
    {
      // Load ALL Colletions
      collection: COLLECTIONS_COLLECTION,
      orderBy: ['weight'],
    },
    {
      // Load ALL Simulations
      collection: SIMULATIONS_COLLECTION,
    },
  ]),
  connect(
    ({ firestore: { data, ordered }, firebase: { auth, profile }, account: { claims, searchItems } }) => ({
      auth,
      profile,
      claims,
      searchItems,
      config: path([WEBSITE_CONFIG_COLLECTION, WEBSITE_CONFIG_ID], data),
      collections: path([COLLECTIONS_COLLECTION], ordered),
      collectionsById: path([COLLECTIONS_COLLECTION], data),
      simulations: path([SIMULATIONS_COLLECTION], data),
    }),
    dispatch => ({
      setSimulation: (collection, simulation, present, forceReload, runTrigger, nextUrl) =>
        dispatch(
          AccountActions.AccountSetSimulation(collection, simulation, present, forceReload, runTrigger, nextUrl),
        ),
      setTrigger: trigger => dispatch(AccountActions.AccountSetTrigger({ trigger })),
      setSearch: searchItems => dispatch(AccountActions.AccountSetSearch(searchItems)),
      toggleSearch: isOpen => dispatch(AccountActions.AccountToggleSearch(isOpen)),
    }),
  ),
  withHandlers({
    addTodo: ({ firestore }) => () => firestore.add('todos', { text: 'sample', done: false }),
  }),
  withStyles(searchResultsStyles),
)

const SimulationGrid = styled.div`
  & > div {
    border-top: ${({ theme }) => theme.globals.divider};
    &:last-of-type {
      border-bottom: ${({ theme }) => theme.globals.divider};
    }
  }
`

const SearchResults = ({ collections, simulations, setSearch, toggleSearch, searchItems }) => {
  const filters = []
  const sortType = 'alpha_desc'

  useEffect(() => {
    return () => {
      setSearch([])
    }
  }, [])

  const getSearchItems = groupLabel => {
    return map(
      searchItems.filter(data => data.groupLabel === groupLabel),
      property('key'),
    )
  }

  const getShowSim = (sim, simSearchItems = [], tagSearchItems = []) => {
    if (!sim) return false // hide if sim doesn't exist

    const simsMatched = simSearchItems.length !== 1 ? false : simSearchItems.includes(sim.id)

    let tagsMatched = true
    if (tagSearchItems.length && !sim.tags) {
      tagsMatched = false
    } else if (tagSearchItems.length && sim.tags) {
      for (let i = 0; i < tagSearchItems.length; i++) {
        if (!sim.tags.includes(tagSearchItems[i])) tagsMatched = false
      }
    }

    let showSim = true
    if (simSearchItems.length && tagSearchItems.length) {
      showSim = simsMatched && tagsMatched
    } else if (simSearchItems.length) {
      showSim = simsMatched
    } else if (tagSearchItems.length) {
      showSim = tagsMatched
    }

    const hideProd = PRODUCTION ? sim.hasOwnProperty('hideProd') : false
    const marketingExp = sim.hasOwnProperty('marketingExp')

    return showSim && !hideProd && !marketingExp
  }

  const getSimulations = () => {
    const simSearchItems = getSearchItems('Simulation Names')
    const tagSearchItems = getSearchItems('Tags')

    const simsRendered = []

    Object.values(simulations)
      .sort((val1, val2) => {
        return SORT[sortType].fn(val1.name, val2.name)
      })
      .map(sim => {
        const key = `filtered_${sim.id}`
        if (getShowSim(sim, simSearchItems, tagSearchItems)) {
          simsRendered.push(
            <SimulationBanner
              id={sim.id}
              key={key}
              clickId={key}
              simulation={sim}
              collections={collections}
              activeFilters={filters}
            />,
          )
        }
      })

    return simsRendered
  }

  const filtered = []
  if (collections) {
    Object.keys(collections).map(collectionKey => {
      const { type } = collections[collectionKey]
      if (!filtered.includes(type)) filtered.push(type)
    })
  }

  if (!collections || !simulations) return null

  const renderedSims = getSimulations()

  return (
    <L bgColor="#F9F9FB" grow>
      <L container pT={3}>
        <L display="inline-grid" columns="max-content" mB={2}>
          <L
            display="grid"
            flow="column"
            ai="center"
            jc="flex-start"
            selectable
            color="secondaryHueLightish"
            onMouseUp={() => {
              setSearch([])
              toggleSearch(false)
            }}
          >
            <IconArrowLeftDark />
            <Type variant="subtitle">Back</Type>
          </L>
        </L>
      </L>
      <L position="relative">
        <L container>
          {renderedSims.length ? (
            <SimulationGrid>{getSimulations()}</SimulationGrid>
          ) : (
            <L display="grid" ji="center" gap={1.5} textAlign="center" pY={5}>
              <IconLogoSimple />
              <Type variant="title">We can't find a match</Type>
              <Type variant="body1">Try removing a term to get more results.</Type>
            </L>
          )}
        </L>
      </L>
    </L>
  )
}

export default enhance(SearchResults)
